import { Card, Flex, Image } from 'antd';
import React, { useMemo } from 'react';

const Medias = ({ productDetails }) => {
  // Fonction pour transformer les URLs CDN en fonction de l'environnement
  const transformCdnUrl = (url) => {
    if (!url) return url;

    // En production, on garde les URLs telles quelles
    if (import.meta.env.VITE_NODE_ENV === 'production') {
      return url;
    }

    // En développement/staging, on remplace les URLs sandbox par les URLs de production
    const cdnMappings = [
      {
        sandbox: 'cdn.sandbox.hoortrade.com/casanoov',
        production: 'cdn.casanoov.com',
      },
      {
        sandbox: 'cdn.sandbox.hoortrade.com/sicaan',
        production: 'cdn.sicaan.com',
      },
      {
        sandbox: 'cdn.sandbox.hoortrade.com/cazeboo',
        production: 'cdn.cazeboo.com',
      },
    ];

    let transformedUrl = url;
    cdnMappings.forEach(({ sandbox, production }) => {
      if (transformedUrl.includes(sandbox)) {
        transformedUrl = transformedUrl.replace(sandbox, production);
      }
    });

    return transformedUrl;
  };

  // Séparer les médias par type et trier par image_order
  const images = useMemo(() => {
    if (!productDetails?.medias) return [];
    return productDetails.medias
      .filter((media) => media.type === 'image')
      .sort((a, b) => a.image_order - b.image_order)
      .map((media) => ({
        ...media,
        cdn_link: transformCdnUrl(media.cdn_link),
      }));
  }, [productDetails?.medias]);

  const documents = useMemo(() => {
    if (!productDetails?.medias) return [];
    return productDetails.medias
      .filter((media) => media.type === 'document')
      .sort((a, b) => a.image_order - b.image_order)
      .map((media) => ({
        ...media,
        cdn_link: transformCdnUrl(media.cdn_link),
      }));
  }, [productDetails?.medias]);

  const videos = useMemo(() => {
    if (!productDetails?.medias) return [];
    return productDetails.medias
      .filter((media) => media.type === 'video')
      .sort((a, b) => a.image_order - b.image_order)
      .map((media) => ({
        ...media,
        cdn_link: transformCdnUrl(media.cdn_link),
      }));
  }, [productDetails?.medias]);

  return (
    <div style={{ padding: '0 24px 24px 24px' }}>
      <Flex vertical gap={16}>
        <Card title={`Images (${images.length})`}>
          {images.length > 0 ? (
            <Image.PreviewGroup
              preview={{
                onChange: (current, prev) =>
                  console.log(`current index: ${current}, prev index: ${prev}`),
              }}
            >
              <Flex wrap="wrap" gap={16}>
                {images.map((image, index) => (
                  <div key={image.id} style={{ position: 'relative' }}>
                    <Image
                      width={200}
                      height={200}
                      style={{ objectFit: 'contain' }}
                      src={image.cdn_link ?? image.local_file}
                      alt={image.name}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        background: '#22247B',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                    >
                      {index + 1}
                    </div>
                  </div>
                ))}
              </Flex>
            </Image.PreviewGroup>
          ) : (
            <div style={{ color: 'rgba(0, 0, 0, 0.45)', textAlign: 'center' }}>
              Aucune image disponible
            </div>
          )}
        </Card>
        <Card title={`Documents (${documents.length})`}>
          {documents.length > 0 ? (
            <Flex vertical gap={8}>
              {documents.map((doc, index) => (
                <div
                  key={doc.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 12px',
                    background: '#f5f5f5',
                    borderRadius: 4,
                  }}
                >
                  <div
                    style={{
                      background: '#22247B',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 500,
                      minWidth: 32,
                      textAlign: 'center',
                    }}
                  >
                    {index + 1}
                  </div>
                  <a
                    href={doc.cdn_link ?? doc.local_file}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ flex: 1 }}
                  >
                    {doc.name}
                  </a>
                  <span
                    style={{
                      fontSize: 12,
                      color: 'rgba(0, 0, 0, 0.45)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {doc.format}
                  </span>
                </div>
              ))}
            </Flex>
          ) : (
            <div style={{ color: 'rgba(0, 0, 0, 0.45)', textAlign: 'center' }}>
              Aucun document disponible
            </div>
          )}
        </Card>
        <Card title={`Vidéos (${videos.length})`}>
          {videos.length > 0 ? (
            <Flex wrap="wrap" gap={16}>
              {videos.map((video, index) => (
                <div key={video.id} style={{ position: 'relative' }}>
                  <video
                    width={200}
                    height={200}
                    controls
                    style={{ objectFit: 'contain', borderRadius: 4 }}
                  >
                    <source
                      src={video.cdn_link ?? video.local_file}
                      type={`video/${video.format}`}
                    />
                    Votre navigateur ne supporte pas la lecture de vidéos.
                  </video>
                  <div
                    style={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      background: '#22247B',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {index + 1}
                  </div>
                </div>
              ))}
            </Flex>
          ) : (
            <div style={{ color: 'rgba(0, 0, 0, 0.45)', textAlign: 'center' }}>
              Aucune vidéo disponible
            </div>
          )}
        </Card>
      </Flex>
    </div>
  );
};

export default Medias;
